import { Injectable } from '@angular/core';
import { combineLatest, from, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { ActiveEmployerCompanyService } from '../active-employer-company/active-employer-company.service';
import { ApiService } from '../api/api.service';
import { BrandsService } from '../brands/brands.service';
import { TEAM_ADMIN_COMPANY_OFFERS_FLAG } from '../managed-feature-flags/managed-feature-flag-keys';
import { ManagedFeatureFlagsService } from '../managed-feature-flags/managed-feature-flags.service';
import { SessionService } from '../session/session.service';
import { TeamManagerWorkspaceService } from '../team-manager-workspace/team-manager-workspace.service';
import { TeamsService } from '../teams/teams.service';
import { OfferContext } from './company-offers.service';

/** Derives rollout access from the existing selected workspace; it grants no workspace permissions. */
@Injectable({ providedIn: 'root' })
export class CompanyOffersAccessService {
  readonly allowedContext$: Observable<OfferContext | undefined>;

  constructor(
    private company: ActiveEmployerCompanyService, private workspace: TeamManagerWorkspaceService,
    private teams: TeamsService, private brands: BrandsService, private flags: ManagedFeatureFlagsService,
    private session: SessionService, private api: ApiService,
  ) {
    this.allowedContext$ = combineLatest([
      this.company.stateChanges(), this.workspace.stateChanges, this.teams.wellspaceChangedObservable,
    ]).pipe(
      map(() => ({ userId: this.session.getUserId(), context: this.currentContext() })),
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      switchMap(({ context }) => context
        ? from(this.isEnabled(context)).pipe(map(enabled => enabled ? context : undefined), startWith(undefined))
        : of(undefined)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  currentContext(): OfferContext | undefined {
    if (!this.api.isAuthenticated() || !this.session.getUserId()) { return undefined; }
    const brand = this.brands.getCampaignBrand();
    if (this.company.isSupportedSingleCompanyWorkspaceActive()) { return { companyId: this.company.activeCompany.id, brand }; }
    const teamId = this.teams.wellspaceChangedObservable.value?.id;
    return this.workspace.managesTeam(teamId) ? { teamId, brand } : undefined;
  }

  async isEnabled(context = this.currentContext(), forceRefresh = false): Promise<boolean> {
    const userId = this.session.getUserId();
    if (!context || !userId || !this.api.isAuthenticated()) { return false; }
    try {
      const enabled = await this.flags.isEnabled(TEAM_ADMIN_COMPANY_OFFERS_FLAG, 'team-admin', {
        userId, companyId: context.companyId, teamId: context.teamId,
      }, forceRefresh, 'exact');
      return enabled === true && userId === this.session.getUserId() && this.api.isAuthenticated()
        && JSON.stringify(context) === JSON.stringify(this.currentContext());
    } catch (_) {
      return false;
    }
  }
}
